import type { OnInit, Signal } from '@angular/core';
import { Component, inject, Injector, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxBadgeComponent,
  OnyxButtonComponent,
  OnyxDataPreviewComponent,
  OnyxDataPreviewItemComponent,
  OnyxFormMode,
  OnyxIconButtonComponent,
  OnyxModalService,
  OnyxNamePipe,
  OnyxPhonePipe,
  OnyxSectionComponent,
} from '@onyx/angular';
import { cloneDeep } from 'lodash';
import { map } from 'rxjs';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import type {
  ContractorContactPersonForm,
  ContractorContactPersonFormGroup,
  ContractorContactPersonModalData,
} from './contractor-contact-person-modal/contractor-contact-person-modal.component';
import { ContractorContactPersonModalComponent } from './contractor-contact-person-modal/contractor-contact-person-modal.component';

export type ContractorContactPersonsFormArray = ReturnType<
  typeof ContractorContactPersonsFormComponent.buildForm
>;

@Component({
  selector: 'app-contractor-contact-persons-form',
  imports: [
    OnyxSectionComponent,
    OnyxButtonComponent,
    OnyxDataPreviewComponent,
    OnyxDataPreviewItemComponent,
    OnyxBadgeComponent,
    OnyxIconButtonComponent,
    TranslatePipe,
    OnyxPhonePipe,
    OnyxNamePipe,
  ],
  templateUrl: './contractor-contact-persons-form.component.html',
  styleUrl: './contractor-contact-persons-form.component.scss',
})
export class ContractorContactPersonsFormComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly modalService = inject(OnyxModalService);
  private readonly fb = inject(NonNullableFormBuilder);

  public readonly form = input.required<ContractorContactPersonsFormArray>();

  protected readonly I18N = 'contractors.contractorForm.contactPersons';
  protected readonly DictionaryCode = DictionaryCode;
  protected readonly OnyxFormMode = OnyxFormMode;

  protected contactPersons!: Signal<ContractorContactPersonForm[] | null>;

  public ngOnInit(): void {
    this.contactPersons = toSignal(
      this.form().valueChanges.pipe(map(() => this.form().getRawValue())),
      {
        initialValue: this.form().getRawValue() ?? null,
        injector: this.injector,
      },
    );
  }

  protected openContactPersonModal(data: ContractorContactPersonModalData) {
    this.modalService
      .open<ContractorContactPersonModalData, ContractorContactPersonFormGroup>(
        ContractorContactPersonModalComponent,
        data,
      )
      .result.subscribe((person) => {
        if (!person) return;

        if (data.mode === OnyxFormMode.ADD) {
          this.form().push(person);
        } else {
          this.form().at(data.index).setValue(person.getRawValue());
        }
      });
  }

  protected addContactPerson(): void {
    this.openContactPersonModal({
      mode: OnyxFormMode.ADD,
      contactPerson: ContractorContactPersonModalComponent.buildForm(this.fb),
    });
  }

  protected editContactPerson(index: number): void {
    this.openContactPersonModal({
      mode: OnyxFormMode.EDIT,
      contactPerson: cloneDeep(this.form().at(index)),
      index,
    });
  }

  protected removeContactPerson(index: number): void {
    this.form().removeAt(index);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.array<ContractorContactPersonFormGroup>([]);
  }
}
